<template>
    <mobile-template title="Planning">

        <template #header-left>
            <div>
                <p class="text-xl font-bold text-gray-900">Planning</p>
                <p class="text-xs text-gray-400">
                    {{ events.length }} événement{{ events.length > 1 ? 's' : '' }}
                </p>
            </div>
        </template>

        <!-- SÉLECTEUR SEMAINE + JOURS -->
        <div class="bg-gray-950 select-none fixed top-0 left-0 right-0 z-10">

            <!-- Navigation semaine — rangée unique condensée -->
            <div class="flex items-center gap-3 px-4 pt-3 pb-2.5">
                <button
                    class="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 active:bg-white/20 transition-colors shrink-0"
                    @click="prevWeek"
                >
                    <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>

                <!-- Scroll horizontal des jours -->
                <div class="flex-1">
                    <div class="flex justify-between gap-1">
                        <button
                            v-for="day in openWeekDays"
                            :key="day.date"
                            class="flex flex-col items-center px-3 py-2 rounded-xl transition-all duration-150 active:scale-95 shrink-0"
                            :class="selectedDate === day.date
                                ? 'bg-orange-500'
                                : day.isToday
                                    ? 'bg-white/15 ring-1 ring-orange-400/60'
                                    : 'bg-white/8'"
                            @click="selectDate(day.date)"
                        >
                            <span
                                class="text-[9px] font-black tracking-widest uppercase leading-none"
                                :class="selectedDate === day.date ? 'text-orange-100' : 'text-white/40'"
                            >{{ day.label }}</span>
                            <span
                                class="text-lg font-black leading-none mt-0.5"
                                :class="selectedDate === day.date ? 'text-white' : day.isToday ? 'text-orange-300' : 'text-white/75'"
                            >{{ day.day }}</span>
                            <span
                                v-if="day.isToday"
                                class="mt-1 text-[7px] font-black px-1 py-px rounded-full tracking-wider leading-none"
                                :class="selectedDate === day.date ? 'bg-white/20 text-white' : 'bg-orange-500 text-white'"
                            >AUJ</span>
                            <div v-else class="mt-1 h-2 flex items-center">
                                <div
                                    v-if="selectedDate === day.date"
                                    class="w-1 h-1 bg-white/50 rounded-full"
                                ></div>
                            </div>
                        </button>
                    </div>
                </div>

                <button
                    class="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 active:bg-white/20 transition-colors shrink-0"
                    @click="nextWeek"
                >
                    <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
            </div>

            <!-- Semaine label + retour aujourd'hui -->
            <div class="flex items-center justify-between px-4 pb-3">
                <button class="flex items-center gap-1.5" @click="goToCurrentWeek">
                    <span class="text-white/40 text-[10px] font-semibold tracking-wider">{{ weekLabel }}</span>
                    <span v-if="isCurrentWeek" class="text-[8px] font-black text-orange-400 tracking-widest uppercase">● Actuelle</span>
                    <span v-else class="text-[8px] font-bold text-white/25 tracking-widest uppercase">↩ Revenir</span>
                </button>
                <span class="text-[10px] font-bold text-white/25">
                    {{ events.length }} événement{{ events.length > 1 ? 's' : '' }}
                </span>
            </div>
        </div>

        <!-- LISTE DES ÉVÉNEMENTS -->
        <div class="p-4 space-y-3 bg-gray-50 min-h-screen pt-[120px]">

            <!-- LOADER -->
            <div v-if="loading" class="flex flex-col justify-center items-center py-24">
                <div class="loader-home"></div>
                <div class="text-xs text-slate-700 text-center mt-4 font-light">Chargement...</div>
            </div>

            <!-- VIDE -->
            <div v-else-if="events.length === 0" class="flex flex-col items-center justify-center py-24 text-center px-8">
                <div class="w-20 h-20 rounded-3xl bg-white border border-gray-200 shadow-sm flex items-center justify-center mb-5">
                    <svg class="w-10 h-10 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                </div>
                <p class="text-lg font-bold text-gray-700">Journée libre</p>
                <p class="text-sm text-gray-400 mt-1">Aucun événement prévu ce jour-là.</p>
            </div>

            <!-- EVENTS -->
            <template v-else>
                <div
                    v-for="item in events"
                    :key="item.id"
                    class="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100"
                    :class="item.statutCode === 'en_cours' ? 'ring-2 ring-yellow-400' : ''"
                >
                    <!-- BARRE COLORÉE GAUCHE + HEADER -->
                    <div
                        class="flex items-stretch"
                        :class="item.statutCode === 'termine' ? 'opacity-60' : ''"
                    >
                        <!-- Barre latérale statut -->
                        <div
                            class="w-1 shrink-0 rounded-l-2xl"
                            :class="item.type === 'chantier'
                                ? 'bg-orange-500'
                                : item.statutCode === 'termine'
                                    ? 'bg-gray-300'
                                    : item.statutCode === 'en_cours'
                                        ? 'bg-yellow-400'
                                        : 'bg-orange-500'"
                        ></div>

                        <div class="flex-1 p-4 space-y-3">

                            <!-- TYPE + STATUT / MOMENT -->
                            <div class="flex items-center justify-between gap-2">
                                <!-- Badge type -->
                                <div class="flex items-center gap-2">
                                    <div
                                        class="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                                        :class="item.type === 'chantier' ? 'bg-orange-100' : 'bg-orange-100'"
                                    >
                                        <!-- Chantier icon -->
                                        <i v-if="item.type === 'chantier'" class="fas fa-helmet-safety text-orange-600"></i>
                                        <!-- Intervention icon -->
                                        <svg v-else class="w-4 h-4 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </div>
                                    <span class="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        {{ item.type === 'chantier' ? 'Chantier' : 'Intervention' }}
                                    </span>
                                </div>

                                <!-- Statut intervention / Moment chantier -->
                                <span
                                    v-if="item.type === 'intervention' && item.statutCode === 'termine'"
                                    class="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-500"
                                >Terminée</span>
                                <span
                                    v-else-if="item.type === 'intervention'"
                                    class="px-2.5 py-1 rounded-full text-xs font-bold border"
                                    :style="{ color: item.statutCouleur, borderColor: item.statutCouleur + '60', background: item.statutCouleur + '15' }"
                                >{{ item.statutLibelle }}</span>
                                <span v-else-if="item.type === 'chantier'" class="px-2.5 py-1 rounded-full text-xs font-bold bg-orange-50 text-orange-600">
                                    {{ item.momentDay === 'morning' ? 'Matin' : item.momentDay === 'afternoon' ? 'Après-midi' : 'Journée' }}
                                </span>
                            </div>

                            <!-- HEURE -->
                            <div class="flex items-center gap-2">
                                <svg class="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 6v6l4 2" stroke-linecap="round" />
                                </svg>
                                <span class="text-base font-black text-gray-900 tracking-tight">
                                    {{ item.start }} – {{ item.end }}
                                </span>
                                <span class="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                    {{ formatDuration(item.estimation) }}
                                </span>
                            </div>

                            <!-- Référence (intervention) -->
                            <p v-if="item.type === 'intervention'" class="text-[11px] font-mono text-gray-400 tracking-wider -mt-1">
                                {{ item.reference }}
                            </p>

                            <!-- Client (intervention) -->
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-2.5">
                                    <div class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                        <svg class="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                                        </svg>
                                    </div>
                                    <span class="text-sm font-bold text-gray-900">{{ item.client }}</span>
                                </div>
                                <a
                                    v-if="item.statutCode !== 'termine' && item.clientPhone"
                                    :href="`tel:${item.clientPhone}`"
                                    class="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center active:bg-green-100 transition-colors"
                                >
                                    <svg class="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
                                    </svg>
                                </a>
                            </div>

                            <!-- Adresse -->
                            <div v-if="item.address" class="flex items-start gap-2.5 bg-gray-50 rounded-xl px-3 py-2.5">
                                <svg class="w-4 h-4 text-gray-400 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                </svg>
                                <p class="text-xs font-semibold text-gray-500 leading-snug">{{ item.address }}</p>
                            </div>

                            <!-- Type + appareil (intervention) -->
                            <div v-if="item.type === 'intervention'" class="flex flex-wrap gap-2">
                                <span class="px-3 py-1 bg-orange-50 text-orange-700 text-xs font-bold rounded-full border border-orange-100">
                                    {{ item.interventionType }}
                                </span>
                                <span v-if="item.appareilType" class="flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-500 text-xs font-semibold rounded-full">
                                    <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M12 15a3 3 0 100-6 3 3 0 000 6z"/>
                                        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                                    </svg>
                                    {{ item.appareilType }}
                                </span>
                            </div>

                            <!-- Notes -->
                            <div v-if="item.type === 'intervention' && item.notes" class="flex items-start gap-2.5 bg-amber-50 border border-amber-100 p-3 rounded-xl">
                                <svg class="w-4 h-4 text-amber-500 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                                </svg>
                                <p class="text-xs font-semibold text-amber-700 line-clamp-2">{{ item.notes }}</p>
                            </div>

                            <!-- CTA Intervention -->
                            <button
                                v-if="item.type === 'intervention' && item.statutCode !== 'termine'"
                                class="w-full flex items-center justify-between px-5 py-4 rounded-xl text-white text-sm font-black tracking-wide transition-all active:scale-98"
                                :class="item.statutCode === 'en_cours'
                                    ? 'bg-yellow-400 text-yellow-950'
                                    : 'bg-gray-950'"
                                @click="$router.push(`/intervention/${item.interventionId}`)"
                            >
                                <span>{{ item.statutCode === 'en_cours' ? '▶ Reprendre l\'intervention' : 'Ouvrir l\'intervention' }}</span>
                                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>

                            <!-- CTA Chantier -->
                            <button
                                v-if="item.type === 'chantier'"
                                class="w-full flex items-center justify-between px-5 py-4 rounded-xl text-white text-sm font-black tracking-wide bg-orange-600 active:bg-orange-700 transition-all active:scale-98"
                                @click="$router.push(`/chantier/${item.chantierId}/list`)"
                            >
                                <span>Ouvrir le chantier</span>
                                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>

                        </div>
                    </div>
                </div>
            </template>
        </div>

    </mobile-template>
</template>

<script>
const DAY_NAME_TO_JS = {
    dimanche: 0, lundi: 1, mardi: 2, mercredi: 3,
    jeudi: 4, vendredi: 5, samedi: 6,
};

export default {
    name: 'PlanningView',

    data() {
        return {
            selectedDate: this.getTodayISO(),
            weekStart: this.getMonday(new Date()),
            events: [],
            loading: false,
            currentRequestId: 0,
        };
    },

    computed: {
        openDayNumbers() {
            const raw = this.$account?.account?.societe?.open_days ?? [];
            return raw.map(n => DAY_NAME_TO_JS[n.toLowerCase()]).filter(n => n !== undefined);
        },

        openWeekDays() {
            const todayISO = this.getTodayISO();
            const days = [];
            for (let i = 0; i < 7; i++) {
                const d = new Date(this.weekStart);
                d.setDate(d.getDate() + i);
                if (!this.openDayNumbers.includes(d.getDay())) continue;
                const iso = this.formatDateLocal(d);
                days.push({
                    date: iso,
                    day: d.getDate(),
                    label: d.toLocaleDateString('fr-FR', { weekday: 'short' }).toUpperCase().slice(0, 3),
                    isToday: iso === todayISO,
                });
            }
            return days;
        },

        weekLabel() {
            const start = new Date(this.weekStart);
            const end = new Date(this.weekStart);
            end.setDate(end.getDate() + 6);
            return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
        },

        isCurrentWeek() {
            return this.formatDateLocal(this.weekStart) === this.formatDateLocal(this.getMonday(new Date()));
        },
    },

    watch: {
        selectedDate() { this.loadEvents(); },
        openWeekDays(days) {
            if (!days.find(d => d.date === this.selectedDate)) {
                this.selectedDate = days[0]?.date ?? this.selectedDate;
            }
        },
    },

    created() { this.loadEvents(); },

    methods: {
        async loadEvents() {
            const userId = this.$account.account.id;
            if (!userId) return;
            const requestId = ++this.currentRequestId;
            this.loading = true;
            try {
                const response = await this.$request.post('/interventions/today', {
                    technicien_id: userId,
                    date: this.selectedDate,
                });
                if (requestId !== this.currentRequestId) return;

                this.events = Array.isArray(response) ? response.map(this.mapEvent) : [];
            } catch (error) {
                if (requestId !== this.currentRequestId) return;
                this.$notify?.error(error?.message || 'Impossible de charger le planning');
            } finally {
                if (requestId === this.currentRequestId) this.loading = false;
            }
        },

        mapEvent(item) {
            console.log(item);
            
            const base = {
                id: item.id,
                type: item.type,
                start: item.heure_debut ? item.heure_debut.substring(0, 5).replace(':', 'h') : '',
                end: item.heure_fin ? item.heure_fin.substring(0, 5).replace(':', 'h') : '',
                estimation: item.estimation,
                momentDay: item.moment_day,
                client: `${item.chantier?.client?.nom ?? ''} ${item.chantier?.client?.prenom ?? ''}`.trim(),
                clientPhone: item.chantier?.client?.telephone ?? null,
                address: item.chantier?.adresse ? `${item.chantier?.adresse.adresse1}, ${item.chantier?.adresse.code_postal} ${item.chantier?.adresse.ville}` : null,
                chantierId: item.chantier_id,
            };
            if (item.type === 'intervention' && item.intervention) {
                const inv = item.intervention;
                return {
                    ...base,
                    interventionId: inv.id,
                    reference: inv.reference,
                    interventionType: inv.intervention_type?.nom ?? 'Type inconnu',
                    appareilType: inv.appareil?.app_type?.label ?? null,
                    statutCode: inv.statut?.code ?? null,
                    statutLibelle: inv.statut?.libelle ?? null,
                    statutCouleur: inv.statut?.couleur ?? null,
                    notes: inv.notes_internes ?? null,
                };
            }
            if (item.type === 'chantier' && item.chantier) {
                return {
                    ...base,
                    chantierNom: item.chantier?.nom ?? `Chantier #${item.chantier_id}`,
                };
            }
            return base;
        },

        selectDate(date) { this.selectedDate = date; },
        prevWeek() {
            const d = new Date(this.weekStart);
            d.setDate(d.getDate() - 7);
            this.weekStart = d;
        },
        nextWeek() {
            const d = new Date(this.weekStart);
            d.setDate(d.getDate() + 7);
            this.weekStart = d;
        },
        goToCurrentWeek() {
            this.weekStart = this.getMonday(new Date());
            this.selectedDate = this.getTodayISO();
        },
        getTodayISO() { return this.formatDateLocal(new Date()); },
        getMonday(date) {
            const d = new Date(date);
            d.setHours(12, 0, 0, 0);
            const day = d.getDay() || 7;
            d.setDate(d.getDate() - day + 1);
            return d;
        },
        formatDateLocal(date) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        },
        formatDuration(minutes) {
            if (!minutes) return '–';
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
        },
    },
};
</script>